import { useCallback, useState } from 'react';
import JSZip from 'jszip';

interface FileDropzoneProps {
  onFilesReady: (files: File[]) => void;
  isLoading?: boolean;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function FileDropzone({ onFilesReady, isLoading }: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    setError(null);
    const files = Array.from(fileList);

    // 檢查是否有 ZIP 檔案
    const zipFile = files.find(f => f.name.toLowerCase().endsWith('.zip'));

    if (zipFile) {
      // 處理 ZIP 檔案
      if (zipFile.size > MAX_FILE_SIZE) {
        setError(`ZIP 檔案過大 (最大 ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
        return;
      }

      try {
        const zip = await JSZip.loadAsync(zipFile);
        const extractedFiles: File[] = [];

        for (const [filename, zipEntry] of Object.entries(zip.files)) {
          // 跳過目錄和系統檔案
          if (zipEntry.dir) continue;
          if (filename.startsWith('__MACOSX/')) continue;
          if (filename.startsWith('.')) continue;
          if (filename.includes('.DS_Store')) continue;
          if (filename.includes('com.apple.')) continue;

          // 只處理 .txt 和 .md 檔案
          const ext = filename.toLowerCase();
          if (!ext.endsWith('.txt') && !ext.endsWith('.md')) continue;

          const content = await zipEntry.async('blob');
          const file = new File([content], filename, { type: 'text/plain' });
          extractedFiles.push(file);
        }

        if (extractedFiles.length === 0) {
          setError('ZIP 檔案中沒有找到有效的 .txt 或 .md 檔案');
          return;
        }

        onFilesReady(extractedFiles);
      } catch {
        setError('無法解壓 ZIP 檔案');
      }
    } else {
      // 處理多個文字檔
      const validFiles = files.filter(f => {
        const ext = f.name.toLowerCase();
        return ext.endsWith('.txt') || ext.endsWith('.md');
      });

      if (validFiles.length === 0) {
        setError('請上傳 .txt、.md 或 .zip 檔案');
        return;
      }

      // 檢查檔案大小
      const totalSize = validFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_FILE_SIZE) {
        setError(`檔案總大小過大 (最大 ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
        return;
      }

      onFilesReady(validFiles);
    }
  }, [onFilesReady]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  }, [processFiles]);

  return (
    <div className="space-y-4">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-card p-8 text-center cursor-pointer
          transition-colors duration-200
          ${isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
          }
          ${isLoading ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <input
          type="file"
          id="file-input"
          multiple
          accept=".txt,.md,.zip"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isLoading}
        />
        <label htmlFor="file-input" className="cursor-pointer">
          <div className="space-y-2">
            <div className="text-4xl">📁</div>
            <div className="text-lg font-medium text-neutral-700 dark:text-neutral-200">
              {isDragging ? '放開以上傳' : '拖拽檔案到此處'}
            </div>
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              或點擊選擇檔案
            </div>
            <div className="text-xs text-neutral-400 dark:text-neutral-500 mt-2">
              支援 .txt、.md 檔案或 ZIP 壓縮包
            </div>
          </div>
        </label>
      </div>

      {error && (
        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 px-4 py-3 rounded-button">
          {error}
        </div>
      )}
    </div>
  );
}
